/**
 * Dai DTO dell'offers-explorer agli oggetti dello schema dataLayer.
 *
 * Attenzione alla semantica dei prezzi, invertita rispetto a quella intuitiva:
 * `ecommerce.items[].price` è il prezzo di LISTINO, mentre `ecommerce.value` è
 * il prezzo promozionale/corrente. GA4 non sottrae mai `discount` da `price`.
 */
import {
  CURRENCY,
  OFFER_ACQUISITION_TYPE,
  PRODUCT_TYPE,
  RENT_OFFER_TYPE,
  SITE_ORIGIN,
  VEHICLE_CONDITION,
} from './constants'
import { getBrandRules } from './brandRules'
import { GtmPageMeta, OfferData, PageType, ProductItem } from './types'

/** Converte in numero positivo; qualunque altro caso diventa `undefined` (chiave omessa). */
export function toNumber(value?: string | number | null): number | undefined {
  if (value === null || value === undefined || value === '') return undefined

  const parsed = typeof value === 'number' ? value : parseFloat(`${value}`)

  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

const absoluteUrl = (path?: string): string | undefined =>
  path ? `${SITE_ORIGIN}${path.split('?')[0].split('#')[0]}` : undefined

const withQuery = (path?: string): string | undefined => (path ? `${SITE_ORIGIN}${path}` : undefined)

interface ItemOptions {
  /** `router.asPath` quando si è sulla pagina prodotto. */
  path?: string
  /** Posizione nella lista, per view_item_list. */
  index?: number
}

const sellOfferPath = (offer: {
  brandCode?: string
  modelCode?: string
  code: string
  vehicle?: OfferGlobal.Vehicle
}): string =>
  `/offerte-auto/${offer.vehicle?.brandCode ?? offer.brandCode}/${
    offer.vehicle?.modelCode ?? offer.modelCode
  }/${offer.code}`

const rentOfferPath = (offer: {
  brandCode?: string
  modelCode?: string
  code: string
  vehicle?: OfferGlobal.Vehicle
}): string =>
  `/noleggio-a-lungo-termine/${offer.vehicle?.brandCode ?? offer.brandCode}/${
    offer.vehicle?.modelCode ?? offer.modelCode
  }/${offer.code}`

export function sellOfferToProductItem(
  offer: OfferGlobal.SellOfferDetail | OfferGlobal.SellOfferItem,
  { path, index }: ItemOptions = {},
): ProductItem {
  const detail = offer as OfferGlobal.SellOfferDetail
  const { vehicle } = detail
  const brand = vehicle?.brand ?? offer.brand
  const model = vehicle?.model ?? offer.model
  const version = vehicle?.version ?? offer.version
  const fuel = vehicle?.engine?.fuel
  const canonicalPath = path || sellOfferPath(offer)

  return {
    item_id: offer.code,
    item_name: [brand, model, fuel].filter(Boolean).join(' ') || undefined,
    item_brand: brand,
    item_variant: model,
    item_category: fuel,
    item_category2: VEHICLE_CONDITION,
    item_category3: vehicle?.category,
    item_category4: detail.year ? `${detail.year}` : undefined,
    item_category5: version,
    price: toNumber(offer.price),
    currency: CURRENCY,
    discount: toNumber(detail.priceReduction),
    image_url: detail.image,
    item_url: withQuery(canonicalPath),
    product_url: absoluteUrl(canonicalPath),
    product_type: PRODUCT_TYPE,
    index,
  }
}

export function rentOfferToProductItem(
  offer: OfferGlobal.RentOfferDetail | OfferGlobal.RentOfferItem,
  { path, index }: ItemOptions = {},
): ProductItem {
  const detail = offer as OfferGlobal.RentOfferDetail
  const item = offer as OfferGlobal.RentOfferItem
  const { vehicle } = detail
  const brand = vehicle?.brand ?? item.brand
  const model = vehicle?.model ?? item.model
  const version = vehicle?.version ?? item.version
  const fuel = vehicle?.engine?.fuel
  const canonicalPath = path || rentOfferPath(offer as any)

  return {
    item_id: offer.code,
    item_name: [brand, model, fuel].filter(Boolean).join(' ') || undefined,
    item_brand: brand,
    item_variant: model,
    item_category: fuel,
    item_category2: VEHICLE_CONDITION,
    item_category3: vehicle?.category ?? item.category,
    item_category5: version,
    // Il noleggio non espone un prezzo di listino del veicolo: resta omesso.
    price: toNumber(detail.specialPrice),
    currency: CURRENCY,
    image_url: detail.images?.[0]?.src ?? item.image,
    item_url: withQuery(canonicalPath),
    product_url: absoluteUrl(canonicalPath),
    product_type: PRODUCT_TYPE,
    index,
  }
}

export function sellOfferToOfferData(offer: OfferGlobal.SellOfferDetail): OfferData {
  const brandCode = offer.vehicle?.brandCode ?? offer.brandCode
  const rules = getBrandRules(brandCode)

  return {
    offer_type: rules.offerType,
    offer_customer_type: rules.offerCustomerType,
    offer_acquisition_type: OFFER_ACQUISITION_TYPE,
    monthly_payment: toNumber(offer.monthlyRate),
  }
}

export function rentOfferToOfferData(offer: OfferGlobal.RentOfferDetail): OfferData {
  return {
    offer_type: RENT_OFFER_TYPE,
    offer_customer_type: offer.customerType === 'piva' ? 'B2B' : 'B2C',
    offer_acquisition_type: OFFER_ACQUISITION_TYPE,
    monthly_payment: toNumber(offer.monthlyRate),
  }
}

/** Prezzo promozionale/corrente del veicolo. */
export const sellOfferValue = (offer: OfferGlobal.SellOfferDetail): number | undefined =>
  toNumber(offer.specialPrice) ?? toNumber(offer.price)

/** Per il noleggio l'unico valore economico esposto è il canone mensile. */
export const rentOfferValue = (offer: OfferGlobal.RentOfferDetail): number | undefined =>
  toNumber(offer.monthlyRate)

interface PageMetaOptions {
  path?: string
  pageTitle?: string
  /** `thank_you_page` per la pageview virtuale post-lead. */
  pageType?: PageType
}

/** Meta di pagina per la VDP sell, pronta per `useGtmPage`. */
export function sellOfferToPageMeta(
  offer: OfferGlobal.SellOfferDetail,
  { path, pageTitle, pageType = 'vdp' }: PageMetaOptions = {},
): GtmPageMeta {
  const { vehicle } = offer
  const brandCode = vehicle?.brandCode ?? offer.brandCode

  return {
    pageType,
    cmsPageId: offer.code,
    pageTitle,
    brand: vehicle?.brand ?? offer.brand,
    model: vehicle?.model ?? offer.model,
    fuel: vehicle?.engine?.fuel,
    segment: vehicle?.categorySlug ?? vehicle?.category,
    rpl: getBrandRules(brandCode).rpl,
    productItem: sellOfferToProductItem(offer, { path }),
    offerData: sellOfferToOfferData(offer),
    value: sellOfferValue(offer),
  }
}

/** Meta di pagina per la VDP noleggio, pronta per `useGtmPage`. */
export function rentOfferToPageMeta(
  offer: OfferGlobal.RentOfferDetail,
  { path, pageTitle, pageType = 'vdp' }: PageMetaOptions = {},
): GtmPageMeta {
  const { vehicle } = offer

  return {
    pageType,
    cmsPageId: offer.code,
    pageTitle,
    brand: vehicle?.brand,
    model: vehicle?.model,
    fuel: vehicle?.engine?.fuel,
    segment: vehicle?.categorySlug ?? vehicle?.category,
    rpl: getBrandRules(vehicle?.brandCode).rpl,
    productItem: rentOfferToProductItem(offer, { path }),
    offerData: rentOfferToOfferData(offer),
    value: rentOfferValue(offer),
  }
}
